import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip);

const brand = {
  deepBlue: '#0a2947',
  charcoal: '#58585b',
  softGray: '#e9e6e1',
};

// Engagement tiers by trailing-30-day activity (messages across all surfaces +
// weighted Claude Code sessions). Order matters (highest first) and each tier's
// `test` must be mutually exclusive.
export const USAGE_TIERS = [
  { key: 'power',   label: 'Power',       range: '200+',   color: '#5e35b1', test: (n) => n >= 200 },
  { key: 'regular', label: 'Regular',     range: '50-199', color: '#0069a6', test: (n) => n >= 50 && n <= 199 },
  { key: 'light',   label: 'Light',       range: '10-49',  color: '#1a7f4e', test: (n) => n >= 10 && n <= 49 },
  { key: 'minimal', label: 'Minimal',     range: '1-9',    color: '#d09b2c', test: (n) => n >= 1 && n <= 9 },
  { key: 'none',    label: 'No Activity', range: '0',      color: '#c0392b', test: (n) => n <= 0 },
];

function tierIndexFor(n) {
  return USAGE_TIERS.findIndex((t) => t.test(n));
}

// The tier a single 30-day activity count falls into (always resolves — the
// last tier catches 0/negative).
export function tierForCount(n) {
  const v = Number(n) || 0;
  return USAGE_TIERS.find((t) => t.test(v)) || USAGE_TIERS[USAGE_TIERS.length - 1];
}

// Filter predicate for a "usage category" dropdown. `tierKey` of 'all' matches
// everyone; otherwise the person's activity must fall in that tier. A null/
// undefined count (no activity data — month view or not configured) never
// matches a specific tier.
export function matchesTier(count, tierKey) {
  if (!tierKey || tierKey === 'all') return true;
  if (count === null || count === undefined) return false;
  return tierForCount(count).key === tierKey;
}

/**
 * Small pill labelling one person's engagement tier by 30-day activity.
 * Renders nothing when `count` is null/undefined (no activity data — e.g. a
 * specific-month view or reporting not configured).
 */
export function TierBadge({ count, showRange }) {
  if (count === null || count === undefined) return null;
  const t = tierForCount(count);
  return (
    <span
      title={`${t.label} · ${t.range} activity (30d)`}
      style={{
        background: `${t.color}1f`, color: t.color, border: `1px solid ${t.color}59`,
        padding: '2px 8px', borderRadius: 999, fontSize: '0.66rem', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
        display: 'inline-flex', alignItems: 'center', gap: 5, lineHeight: 1.4,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
      {t.label}{showRange ? ` (${t.range})` : ''}
    </span>
  );
}
TierBadge.propTypes = {
  count: PropTypes.number,
  showRange: PropTypes.bool,
};

/**
 * Asterisk flag for a Claude Code user (rendered next to their name). Renders
 * nothing unless `show` is true. Pair with a "* uses Claude Code" footnote.
 */
export function ClaudeCodeMark({ show }) {
  if (!show) return null;
  return (
    <span
      title="Uses Claude Code"
      aria-label="Uses Claude Code"
      style={{ color: '#d09b2c', fontWeight: 800, cursor: 'help', verticalAlign: 'super', fontSize: '0.8em', marginLeft: 1 }}
    >
      *
    </span>
  );
}
ClaudeCodeMark.propTypes = { show: PropTypes.bool };

/**
 * Compact tier indicator: a colored dot (rendered before a name) whose color
 * encodes the engagement tier, with a hover tooltip naming the tier + range.
 * Renders nothing when `count` is null/undefined (no activity data).
 */
export function TierDot({ count }) {
  if (count === null || count === undefined) return null;
  const t = tierForCount(count);
  return (
    <span
      title={`${t.label} (${t.range}) — activity, last 30 days`}
      aria-label={`${t.label} tier`}
      style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: t.color, flexShrink: 0, cursor: 'help' }}
    />
  );
}
TierDot.propTypes = { count: PropTypes.number };

/**
 * Donut of team members bucketed into engagement tiers by 30-day activity.
 * `counts` is an array of per-person activity totals (nulls/undefined → 0).
 */
export default function UsageTierChart({ counts, title, subtitle }) {
  const tierCounts = useMemo(() => {
    const out = USAGE_TIERS.map(() => 0);
    (counts || []).forEach((raw) => {
      const n = Number(raw) || 0;
      const i = tierIndexFor(n);
      if (i >= 0) out[i] += 1;
    });
    return out;
  }, [counts]);

  const total = tierCounts.reduce((a, b) => a + b, 0);

  const data = useMemo(() => ({
    labels: USAGE_TIERS.map((t) => `${t.label} (${t.range})`),
    datasets: [{
      data: tierCounts,
      backgroundColor: USAGE_TIERS.map((t) => t.color),
      borderColor: '#ffffff',
      borderWidth: 2,
      hoverOffset: 4,
    }],
  }), [tierCounts]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: {
      legend: { display: false }, // custom legend below shows counts
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const pct = total ? Math.round((ctx.parsed / total) * 100) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  }), [total]);

  return (
    <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.08)', padding: '1.25rem 1.5rem' }}>
      {title && <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0, fontSize: '1.05rem' }}>{title}</h5>}
      {subtitle && <p style={{ color: brand.charcoal, margin: '4px 0 0', fontSize: '0.85rem' }}>{subtitle}</p>}

      {total === 0 ? (
        <div style={{ color: brand.charcoal, fontSize: '0.85rem', padding: '1.5rem 0', textAlign: 'center' }}>
          No usage data to chart yet.
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
          <div style={{ position: 'relative', width: 200, height: 200, flexShrink: 0 }}>
            <Doughnut data={data} options={options} />
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ color: brand.deepBlue, fontWeight: 800, fontSize: '1.5rem', lineHeight: 1 }}>{total}</div>
              <div style={{ color: brand.charcoal, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>people</div>
            </div>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.4rem', flex: '1 1 200px', minWidth: 0 }}>
            {USAGE_TIERS.map((t, i) => (
              <li key={t.key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: brand.charcoal }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: t.color, flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0 }}>{t.label} <span style={{ color: brand.charcoal, opacity: 0.7 }}>({t.range})</span></span>
                <strong style={{ color: brand.deepBlue, fontVariantNumeric: 'tabular-nums' }}>{tierCounts[i]}</strong>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

UsageTierChart.propTypes = {
  counts: PropTypes.arrayOf(PropTypes.number),
  title: PropTypes.string,
  subtitle: PropTypes.string,
};
